/**
 * Shared helpers + path builders for the AppsFlyer API factories. Every
 * route is namespaced under `/integrations/appsflyer` and admin-gated on the
 * BE (RequireRole admin, admin_member).
 */
import { apiClient, ApiError } from '@discovr/common';
import type { DataResponse } from '@discovr/types';

const BASE = '/integrations/appsflyer';

export const brandBase = (brandId: string): string =>
  `${BASE}/brands/${encodeURIComponent(brandId)}`;

export const campaignBase = (campaignId: string): string =>
  `${BASE}/campaigns/${encodeURIComponent(campaignId)}`;

export const adminBase = (): string => `${BASE}/admin`;

/** Read a `DataResponse<T>` envelope; throws ApiError on failure. */
export async function getData<T>(url: string): Promise<T> {
  const res = await apiClient.get<DataResponse<T>>(url);
  return res.data.data;
}

/** Return null on 404 (no integration / no config yet); rethrow otherwise. */
export async function getOrNullOn404<T>(url: string): Promise<T | null> {
  try {
    const res = await apiClient.get<DataResponse<T>>(url);
    return res.data.data ?? null;
  } catch (err) {
    if (err instanceof ApiError && err.httpStatus === 404) return null;
    throw err;
  }
}

/** Build a `?from=&to=` query suffix from an optional date range. */
export function dateRangeQs({ from, to }: { from?: string; to?: string }): string {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const s = qs.toString();
  return s ? `?${s}` : '';
}
